import { useEffect, useState } from 'react';
import { Bug, ImagePlus, Send, X } from 'lucide-react';
import { api, type SessionUser } from './api';

export const FEEDBACK_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
const feedbackScreenshotTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);

export function validateFeedbackScreenshot(file: File): string | null {
  if (!feedbackScreenshotTypes.has(file.type)) return 'Поддерживаются только PNG, JPEG и WebP.';
  if (file.size > FEEDBACK_SCREENSHOT_MAX_BYTES) return 'Размер снимка не должен превышать 5 МиБ.';
  return null;
}

export function FeedbackDialog({ currentUser, onClose }: { currentUser: SessionUser; onClose(): void }): React.JSX.Element {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent): void => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [busy, onClose]);

  const chooseScreenshot = (file: File | undefined): void => {
    setError(null);
    if (!file) {
      setScreenshot(null);
      return;
    }
    const validationError = validateFeedbackScreenshot(file);
    if (validationError) {
      setScreenshot(null);
      setFileInputKey((value) => value + 1);
      setError(validationError);
      return;
    }
    setScreenshot(file);
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.submitFeedback(subject.trim(), description.trim(), screenshot);
      setSubject('');
      setDescription('');
      setScreenshot(null);
      setFileInputKey((value) => value + 1);
      setNotice('Сообщение отправлено. Спасибо — мы передали его администратору.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось отправить сообщение');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={() => { if (!busy) onClose(); }}>
      <section className="dialog feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="dialog-heading">
          <div><span className="eyebrow">Обратная связь</span><h2 id="feedback-title"><Bug size={19} /> Сообщить о проблеме</h2></div>
          <button className="icon-button" type="button" aria-label="Закрыть" onClick={onClose} disabled={busy}><X size={18} /></button>
        </div>
        <p className="auth-form-intro">Опишите, что произошло и что ожидалось. Сообщение будет отправлено от имени {currentUser.email}.</p>
        <form className="dialog-form feedback-form" onSubmit={(event) => void submit(event)}>
          <label><span>Тема</span><input value={subject} onChange={(event) => setSubject(event.target.value)} minLength={3} maxLength={120} autoFocus required /></label>
          <label><span>Описание</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={7} minLength={10} maxLength={5_000} required /></label>
          <label className="feedback-file">
            <span>Снимок экрана — необязательно</span>
            <span className="feedback-file-control"><ImagePlus size={17} />{screenshot ? screenshot.name : 'Выбрать PNG, JPEG или WebP'}<input key={fileInputKey} type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => chooseScreenshot(event.target.files?.[0])} /></span>
            <small>Не более 5 МиБ. Перед отправкой убедитесь, что на снимке нет паролей и других секретов.</small>
          </label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          {notice ? <p className="form-notice" role="status">{notice}</p> : null}
          <button className="primary-action" type="submit" disabled={busy || subject.trim().length < 3 || description.trim().length < 10}><Send size={15} /> {busy ? 'Отправка…' : 'Отправить'}</button>
        </form>
      </section>
    </div>
  );
}
